import { isEmpty } from 'lodash';
import { useRecoilValue } from 'recoil';

import { Heading } from '@/components/heading';
import { Link } from '@/components/link';
import { SchedulesAlternativeRouteCard } from '@/components/modules/schedules/alternative-route-card';
import { Text } from '@/components/text';
import { AdSlot } from '@/components/ui/adslot';
import { ADS_ROUTE_SIDEBAR_SLOT } from '@/lib/constants';
import { RouteSummaryModel } from '@/lib/models/route-summary/types';
import { featuresState } from '@/state/features';
import { PlaceInterface } from '@/types/api-types';
import { buildRouteMapPreview, createRoutePageUrl } from '@/utils/helpers';
import { tv } from '@/utils/styles';

interface Props {
  from: PlaceInterface;
  to: PlaceInterface;
  airRoutes: RouteSummaryModel[];
  oceanRoutes: RouteSummaryModel[];
  roadRoutes: RouteSummaryModel[];
}

const PageRouteSidebar = ({ from, to, airRoutes, oceanRoutes, roadRoutes }: Props) => {
  const features = useRecoilValue(featuresState);
  const ADSLOT_ENABLED = features.showAds;

  const primaryAirRoute = !isEmpty(airRoutes) ? airRoutes[0] : null;
  const primaryOceanRoute = !isEmpty(oceanRoutes) ? oceanRoutes[0] : null;
  const primaryRoadRoute = !isEmpty(roadRoutes) ? roadRoutes[0] : null;

  const { sidebar, sidebarContainer, sidebarHeader, usefulLinks, miniMap, blurb, textLink } = styles();

  return (
    <article className={sidebar()} data-cy="routes-sidebar">
      <div className={sidebarContainer()}>
        <header className={sidebarHeader()}>
          <Heading variant="h5" as="h2">
            Map / Route overview
          </Heading>
        </header>
        <div className={miniMap()}>
          <img src={buildRouteMapPreview(from, to)} alt={`${from.name} to ${to.name}`} />
        </div>
        <div className={sidebarHeader()}>
          <Heading variant="h5" as="h2">
            Compare shipping modes
          </Heading>
        </div>
        {primaryAirRoute && (
          <SchedulesAlternativeRouteCard route={primaryAirRoute} showScheduleLink={false} customTitle="Air Freight" />
        )}
        {primaryOceanRoute && (
          <SchedulesAlternativeRouteCard
            route={primaryOceanRoute}
            showScheduleLink={false}
            customTitle="Container Ship"
          />
        )}
        {primaryRoadRoute && (
          <SchedulesAlternativeRouteCard route={primaryRoadRoute} showScheduleLink={false} customTitle="Road Freight" />
        )}
      </div>
      <div>{ADSLOT_ENABLED && <AdSlot slotId={ADS_ROUTE_SIDEBAR_SLOT} format="square" />}</div>
      <div className={blurb()}>
        <Heading variant="h5" className="mb-1">
          About Fluent Cargo
        </Heading>
        <Text variant="sm" className="m-0">
          Fluent Cargo is shipment and transport planning tool that is helping to digitize the global freight industry.
          See all your cargo options in one place and plan your next international shipment in seconds.
        </Text>
      </div>
      <footer className={usefulLinks()}>
        <Heading variant="h5" className="mb-1">
          More useful links
        </Heading>
        <div>
          <Link href="#faqs" className={textLink()}>
            Frequently asked questions
          </Link>
        </div>
        <div>
          <Link href="#from-links" className={textLink()}>
            Alternative ports and destinations
          </Link>
        </div>
        <div>
          {to.placeId && from.placeId ? (
            <Link href={createRoutePageUrl(to.placeId, from.placeId)} className={textLink()}>
              {to.name} to {from.name} cargo routes
            </Link>
          ) : null}
        </div>
        <div>
          <Link href="/features" className={textLink()}>
            Fluent Cargo features
          </Link>
        </div>
      </footer>
    </article>
  );
};

const styles = tv({
  slots: {
    sidebar: 'sticky top-4',
    sidebarContainer: 'overflow-hidden rounded-lg border border-grey-300',
    sidebarHeader: 'mt-4 border-b border-dashed border-grey-200 px-4',
    blurb: 'mt-4 mt-6 px-2',
    usefulLinks: 'mt-4 px-2 text-sm',
    textLink: 'no-underline hover:underline',
    miniMap: 'relative w-full pb-[60%] [&_img]:absolute [&_img]:left-0 [&_img]:right-0 [&_img]:top-0',
  },
});

export { PageRouteSidebar };
